import { getAlgoliaRatingQuery } from 'containers/HomePage/services/api-helper';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _has from 'lodash/has';
import _uniqBy from 'lodash/uniqBy';
import { getAlgoliaFilters } from 'containers/Main/utils';
import Config from 'utils/getEnvConfig';
import { ARTICLE_TYPES } from 'utils/clientSettings';
import { getArticlesMinimumWordCount } from 'containers/Resources/utils';

const {
  ALGOLIA: { SEARCH_INDEX },
} = Config;

export const addSectionToSuggestions = (
  suggestions,
  hits,
  sectionTitle,
  suggestionsType,
) => {
  if (hits.length > 0) {
    return suggestions.concat({
      title: sectionTitle,
      suggestions: hits.map(({ nameTitle, url, type, subtype }) => ({
        title: nameTitle,
        url,
        type: suggestionsType || type,
        subtype,
        blog: type === 'Blogs',
      })),
    });
  }
  return suggestions;
};

export const getQueryObject = (indexName, query, filters) => ({
  indexName,
  query,
  params: {
    hitsPerPage: 3,
    filters,
  },
});

export const getFiltersString = (clientDetails, config, locale) => {
  const prefix = locale !== 'en-US' ? `${locale}_` : '';

  const shouldFilterShortArticles = _get(
    clientDetails,
    'metadata.filterShortArticles',
    false,
  );
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const excludedAssessments = _get(
    clientDetails,
    'excludeAssessmentCollection.items',
  );
  const { algoliaAssessmentsQuery = '' } = getAlgoliaFilters({
    excludedAssessments,
  });

  const excludedTopicSlug = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.slug)
    : [];

  const clientQuery = !_isEmpty(_get(clientDetails, 'clientGroup'))
    ? `(clients:"none" OR clients:"${
        clientDetails.shortName
      }" OR clients:"${_get(
        clientDetails,
        'clientGroup.shortName',
      )}") AND NOT clientsExclude:"${
        clientDetails.shortName
      }" AND NOT clientsExclude:"${_get(
        clientDetails,
        'clientGroup.shortName',
      )}"`
    : `(clients:"none" OR clients:"${clientDetails.shortName}") AND NOT clientsExclude:"${clientDetails.shortName}"`;
  let filtersString = clientQuery;

  if (!_isEmpty(excludedTopicSlug)) {
    _forEach(excludedTopicSlug, slug => {
      filtersString += ` AND NOT slug:'${slug}' AND NOT filterTopics:'${slug}'`;
    });
  }

  if (algoliaAssessmentsQuery) {
    filtersString += algoliaAssessmentsQuery;
  }

  if (
    !_isEmpty(_get(clientDetails, 'metadata.hideCostDescriptionResources', []))
  ) {
    filtersString += ` AND ${_get(
      clientDetails,
      'metadata.hideCostDescriptionResources',
      [],
    )
      .map(item => `NOT ${prefix}costDescription:'${item}'`)
      .join(' AND ')}`;
  }

  const averageRatingCutoff = _get(clientDetails, 'averageRatingCutoff');
  const expertRatingCutoff = _get(clientDetails, 'expertRatingCutoff');
  const userRatingCutoff = _get(clientDetails, 'userRatingCutoff');

  const ratingFilter = getAlgoliaRatingQuery({
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
  });
  if (ratingFilter) {
    filtersString += ratingFilter;
  }

  if (shouldFilterShortArticles) {
    const articlesMinimumWordCount = getArticlesMinimumWordCount(
      config,
      clientDetails,
    );
    filtersString += ` AND wordCount > ${articlesMinimumWordCount}`;
  }

  return filtersString;
};

const SEARCH_FIELDS = [
  'nameTitle',
  'relatedTopics',
  'type',
  'aliases',
  'keywords',
  'personOrganization.name',
];

const SERIES_SEARCH_FIELDS = ['nameTitle', 'description'];

export const buildSearchAlgoliaQueries = ({
  filters,
  optionalFilters,
  minProximity,
  query,
  excludedTopics,
  excludeResourceTypes,
  audienceFilters,
  excludedTagsFilters,
  stateFilters,
  alwaysShowInformationalLink,
}) => {
  const restrictSearchableAttributes = SEARCH_FIELDS;
  const seriesRestrictSearchableAttributes = SERIES_SEARCH_FIELDS;
  const excludeResourceTypesFilters = _isEmpty(excludeResourceTypes)
    ? ''
    : ` AND ${excludeResourceTypes
        .map(type => {
          if (type === 'Articles' && alwaysShowInformationalLink) {
            return `(NOT type:"${type}" OR articlesSubtype:"${ARTICLE_TYPES.INFORMATION_LINK}")`;
          }
          return `NOT type:"${type}"`;
        })
        .join(' AND ')}`;
  const excludedTopicsFilters = _isEmpty(excludedTopics)
    ? ''
    : ` AND ${excludedTopics
        .map(topic => `NOT objectID:'${topic}'`)
        .join(' AND ')}`;
  const excludedTopicsResourcesFilters = _isEmpty(excludedTopics)
    ? ''
    : ` AND ${excludedTopics
        .map(topic => `NOT relatedTopics.id:'${topic}'`)
        .join(' AND ')}`;

  const baseQueries = [
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 2,
        filters: `${filters} AND type:'Topics'${excludedTopicsFilters}`,
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes,
        advancedSyntax: true,
        queryType: 'prefixLast',
      },
    },
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 1,
        filters: `${filters} AND type:'Assessments'${audienceFilters}${excludedTopicsResourcesFilters}`,
        optionalFilters,
        getRankingInfo: true,
        exactOnSingleWordQuery: 'word',
        advancedSyntax: true,
        minProximity,
        restrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 3,
        filters: `${filters} AND type:'Insights'${audienceFilters}${excludedTopicsResourcesFilters}`,
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 18,
        filters: `${filters} AND NOT type:'Topics' AND NOT type:'Assessments' AND NOT type:'Insights' AND NOT type:'Series'${excludeResourceTypesFilters} AND NOT displayOnly:true${stateFilters}${excludedTagsFilters}${audienceFilters}${excludedTopicsResourcesFilters}`,
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
    !excludeResourceTypes.includes('Series') && {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 3,
        filters: "type:'Series'",
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes: seriesRestrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
  ].filter(Boolean);

  let dynamicTypoParams = {};
  const queryLength = query.length;

  if (queryLength < 6) {
    dynamicTypoParams = {
      typoTolerance: true,
      minWordSizefor1Typo: 5,
    };
  } else {
    dynamicTypoParams = {
      typoTolerance: true,
      minWordSizefor1Typo: 3,
      minWordSizefor2Typos: 6,
    };
  }

  // Use helper function for query building
  return buildMultiQuerySet(baseQueries, query, dynamicTypoParams);
};

export const getExclusiveBadge = (resource, clientDetails) => {
  const dataClients = _get(resource, 'clients', []).map(el =>
    el?.toLowerCase(),
  );
  const resourceType = _get(resource, 'type');

  const isClientExclusive = dataClients.includes(
    _get(clientDetails, 'shortName')?.toLowerCase(),
  );
  const isClientGroupExclusive = dataClients.includes(
    _get(clientDetails, 'clientGroup.shortName')?.toLowerCase(),
  );

  const showClientBadge =
    isClientExclusive ||
    isClientGroupExclusive ||
    resourceType === 'Client Exclusive';

  const hasCustomBadge =
    _has(clientDetails, 'metadata.clientExclusiveIcon') && isClientExclusive;
  const hasAssetLogo =
    _has(clientDetails, 'logosCollection.items[0].url') && isClientExclusive;
  const isReverseLogoOrder = _get(clientDetails, 'metadata.isReverseLogoOrder');

  const badge = hasCustomBadge
    ? _get(clientDetails, 'metadata.clientExclusiveIcon')
    : hasAssetLogo
    ? _get(clientDetails, 'logosCollection.items[0].url')
    : isReverseLogoOrder
    ? _get(clientDetails, 'crediblemindLogo.url')
    : _get(clientDetails, 'logoReverse.url');

  const badgeTitle = isClientExclusive
    ? _get(clientDetails, 'metadata.clientExclusiveResource.tooltipBadge', '')
    : _get(
        clientDetails,
        'clientGroup.metadata.clientExclusiveResource.tooltipBadge',
        '',
      );

  return {
    hasCustomBadge,
    showClientBadge,
    badge,
    badgeTitle,
  };
};

export const addInternalTyping = (resource, internalType) => ({
  ...resource,
  internalType,
});

export const sortSearchPreviewResources = (resources, previewOrder) => {
  if (_isEmpty(previewOrder)) return resources;
  const groupResources = resources.reduce((result, item) => {
    const { internalType, ...rest } = item;
    const updatedResult = { ...result };

    if (!updatedResult[internalType])
      updatedResult[internalType] = {
        type: internalType,
        order: previewOrder[internalType] || 0,
        content: [],
      };

    updatedResult[internalType].content.push(rest);

    return updatedResult;
  }, {});

  const resourcesTypes = Object.keys(groupResources);

  Object.entries(previewOrder)
    .sort(([, orderA], [, orderB]) => orderB - orderA)
    .forEach(([itemToMove]) => {
      let indexToMove = previewOrder[itemToMove] - 1;

      if (indexToMove !== undefined) {
        if (indexToMove >= resourcesTypes.length || indexToMove < 0) {
          indexToMove = resourcesTypes.length - 1;
        }

        const itemIndex = resourcesTypes.indexOf(itemToMove);
        if (itemIndex !== -1) {
          const [removedItem] = resourcesTypes.splice(itemIndex, 1);
          resourcesTypes.splice(indexToMove, 0, removedItem);
        }
      }
    });

  const sortedResources = resourcesTypes.flatMap(
    type => groupResources[type].content,
  );

  return sortedResources;
};

export function pickTopResources(
  resources,
  finalQuery,
  clientData,
  resourcePriority,
) {
  // eslint-disable-next-line no-underscore-dangle
  const getRankMetric = (obj, k, def = 0) => obj?._rankingInfo?.[k] ?? def;

  const compareRelevance = (a, b) => {
    if (getRankMetric(a, 'nbExactWords') !== getRankMetric(b, 'nbExactWords'))
      return (
        getRankMetric(b, 'nbExactWords') - getRankMetric(a, 'nbExactWords')
      );

    if (getRankMetric(a, 'nbTypos') !== getRankMetric(b, 'nbTypos'))
      return getRankMetric(a, 'nbTypos') - getRankMetric(b, 'nbTypos');

    if (getRankMetric(a, 'words') !== getRankMetric(b, 'words'))
      return getRankMetric(b, 'words') - getRankMetric(a, 'words');

    if (
      getRankMetric(a, 'proximityDistance') !==
      getRankMetric(b, 'proximityDistance')
    )
      return (
        getRankMetric(a, 'proximityDistance') -
        getRankMetric(b, 'proximityDistance')
      );

    return getRankMetric(b, 'userScore') - getRankMetric(a, 'userScore');
  };

  const getPriority = type => resourcePriority?.[type] ?? 99;

  const byPriorityThenScore = (a, b) => {
    const aPriority = getPriority(a.type);
    const bPriority = getPriority(b.type);
    if (aPriority !== bPriority) return aPriority - bPriority;
    return compareRelevance(a, b);
  };

  const sorted = resources.sort(byPriorityThenScore);

  const firstThree = [];
  const seenTypes = [];

  sorted.forEach(res => {
    if (firstThree.length < 3 && !seenTypes.includes(res.type)) {
      firstThree.push(res);
      seenTypes.push(res.type);
    }
  });

  return firstThree.length === 3
    ? firstThree
    : [...firstThree, ...sorted.filter(hit => !firstThree.includes(hit))].slice(
        0,
        3,
      );
}

/**
 * Builds query sets based on query length for optimal typo tolerance
 * For 5-6 character queries: Uses 3-query approach (prefixNone exact, prefixLast typo, prefixNone typo)
 * For other lengths: Uses 2-query approach (exact, typo)
 * @param {Array} baseQueries - Base query configuration array
 * @param {string} query - Search query string
 * @param {Object} dynamicTypoParams - Dynamic typo parameters
 * @returns {Object} Object with queries array and querySetCount
 */
export const buildMultiQuerySet = (baseQueries, query, dynamicTypoParams) => {
  const queryLength = query.length;

  // For queries with length 5-6, use 3-query approach
  if (queryLength === 5 || queryLength === 6) {
    // Query 1: prefixNone + no typo tolerance (exact complete word matches)
    const exactQueries = baseQueries.map(q => ({
      ...q,
      params: {
        ...q.params,
        queryType: 'prefixNone',
        typoTolerance: false,
      },
    }));

    // Query 2: prefixLast + typo tolerance (prefix matches with typo)
    const prefixLastTypoQueries = baseQueries.map(q => ({
      ...q,
      params: {
        ...q.params,
        queryType: 'prefixLast',
        ...dynamicTypoParams,
      },
    }));

    // Query 3: prefixNone + typo tolerance (complete words with typo)
    const prefixNoneTypoQueries = baseQueries.map(q => ({
      ...q,
      params: {
        ...q.params,
        queryType: 'prefixNone',
        typoTolerance: true,
        minWordSizefor1Typo: 5,
      },
    }));

    // Order: exact, prefixLast+typo, prefixNone+typo
    return {
      queries: [
        ...exactQueries,
        ...prefixLastTypoQueries,
        ...prefixNoneTypoQueries,
      ],
      querySetCount: 3,
    };
  }

  // For all other query lengths, use 2-query approach
  const exactQueries = baseQueries.map(q => ({
    ...q,
    params: {
      ...q.params,
      typoTolerance: false,
    },
  }));

  const typoQueries = baseQueries.map(q => ({
    ...q,
    params: {
      ...q.params,
      ...dynamicTypoParams,
    },
  }));

  return {
    queries: [...exactQueries, ...typoQueries],
    querySetCount: 2,
  };
};

/**
 * Selects and deduplicates results based on query set count
 * @param {Array} results - Algolia results array
 * @param {number} index - Base query index
 * @param {number} originalQueryCount - Number of base queries
 * @param {number} querySetCount - Number of query sets (2 or 3)
 * @returns {Object} Selected Algolia result
 */
export const getResultByIndexWithQuerySets = (
  results,
  index,
  originalQueryCount,
  querySetCount,
) => {
  const exactResult = results[index];

  // For 3-query approach, combine all 3 queries
  if (querySetCount === 3) {
    const query1Result = exactResult; // prefixNone + no typo
    const query2Result = results[index + originalQueryCount]; // prefixLast+typo
    const query3Result = results[index + originalQueryCount * 2]; // prefixNone+typo

    // Boost Query 1 results (highest priority - exact matches)
    const query1HitsWithBoost = query1Result.hits.map(hit => ({
      ...hit,
      _querySource: 1,
      // eslint-disable-next-line no-underscore-dangle
      _rankingInfo: {
        // eslint-disable-next-line no-underscore-dangle
        ...hit._rankingInfo,
        nbTypos: 0,
        // eslint-disable-next-line no-underscore-dangle
        userScore: (hit._rankingInfo?.userScore || 0) + 2000,
      },
    }));

    // Boost Query 3 results (medium priority - complete words with typo)
    const query3HitsWithBoost = query3Result.hits.map(hit => ({
      ...hit,
      _querySource: 3,
      // eslint-disable-next-line no-underscore-dangle
      _rankingInfo: {
        // eslint-disable-next-line no-underscore-dangle
        ...hit._rankingInfo,
        nbTypos: 0,
        // eslint-disable-next-line no-underscore-dangle
        userScore: (hit._rankingInfo?.userScore || 0) + 1000,
      },
    }));

    // Query 2 has lowest priority (prefix matches with typo)
    // Filter out results with 3+ typos (very weak matches)
    const query2HitsWithSource = query2Result.hits
      // eslint-disable-next-line no-underscore-dangle
      .filter(hit => (hit._rankingInfo?.nbTypos ?? 0) < 3)
      .map(hit => ({
        ...hit,
        _querySource: 2,
      }));

    // Combine: Query 1 first, then Query 3, then Query 2
    const combinedHits = [
      ...query1HitsWithBoost,
      ...query3HitsWithBoost,
      ...query2HitsWithSource,
    ];

    // Deduplicate by objectID
    const deduplicatedHits = _uniqBy(combinedHits, 'objectID');

    // Return modified result with deduplicated hits
    return {
      ...query3Result,
      hits: deduplicatedHits,
      nbHits: query1Result.nbHits + query2Result.nbHits + query3Result.nbHits,
    };
  }

  // For 2-query approach, return exact if available, otherwise typo
  if (exactResult.nbHits > 0) {
    return exactResult;
  }

  return results[index + originalQueryCount];
};
